import { Model } from 'pinia-orm'

import { Attr, BelongsTo, HasMany, Num, Uid } from 'pinia-orm/dist/decorators'
import { Tank } from 'modules/Tank/Tank'
import { Transaction } from 'modules/Transaction/Transaction'
import { OrgGroup } from 'modules/OrgGroup/OrgGroup'
import { Reseller } from 'modules/Reseller/Reseller'
import { CalibrationOrg } from 'modules/CalibrationOrg/CalibrationOrg'
import { Fluid } from 'modules/Fluid/Fluid'
import { DeviceModel } from 'modules/DeviceModel/DeviceModel'
import { Tenant } from 'modules/Tenant/Tenant'
import { Org } from 'modules/Org/Org'
import { DeviceVersion } from './types/DeviceVersion'

export class Device extends Model {
  static override entity = 'devices'

  @Uid() declare id: string
  @Num(null) declare int_id: number | null

  @Attr(null) declare internal_name: string
  @Attr(null) declare device_number: number
  @Attr(null) declare broadcast_id: string | null
  @Attr(null) declare alias: string | null
  @Attr(null) declare device_model_id: string | null
  @Attr(null) declare serial_number: string | null
  @Attr(null) declare firmware_version: string | null
  @Attr(null) declare hardware_version: DeviceVersion | null
  @Attr(null) declare fluid_name: string | null
  @Attr(null) declare fluid_id: string | null
  @Attr(true) declare is_active: boolean
  @Attr(false) declare is_virtual: boolean
  @Attr(null) declare fluid_uom: string | null
  @Attr(null) declare pulse_make: string | null
  @Attr(null) declare pulse_model: string | null
  @Attr(null) declare pulse_serial: string | null
  @Attr(null) declare notes: string | null
  @Attr(null) declare precision: number | null
  @Attr(null) declare bt_type: string | null
  @Attr(true) declare volume_strapping_table: string
  @Attr(null) declare commission_date: string | null
  @Attr(null) declare install: string | null
  @Attr(null) declare event_sync_type: number | null
  @Attr(null) declare hometown_time_zone: string | null
  @Attr(null) declare description: string | null
  @Attr(null) declare defualt_latitude: number | null
  @Attr(null) declare default_longitude: number | null
  @Attr(true) declare hold_connect: boolean
  @Attr(true) declare update_on_connection: boolean
  @Attr(null) declare fuellox_ini_file_source: string | null
  @Attr(null) declare secure_key: string | null
  @Attr(null) declare reference: string | null
  @Attr(null) declare calibration_org_id: string | null
  @Attr(null) declare last_calibration_datetime: string | null
  @Attr(null) declare last_level_volume: number | null
  @Attr(null) declare last_level_recorded_datetime: string | null
  @Attr(null) declare volume_capacity: number | null
  @Attr(null) declare ullage: number | null
  @Attr(null) declare app_connection_timeout_ms: number | null
  @Attr(null) declare connection_range_warning: number | null
  @Attr(null) declare connection_range_disconnect: number | null
  @Attr(null) declare connect_no_start_timeout_ms: number | null
  @Attr(null) declare collect_meta: number | null
  @Attr(null) declare transaction_resume_timeout_ms: number | null
  @Attr(null) declare config_updated: string | null
  @Attr(true) declare iotiq_io_device_active: boolean
  @Attr(null) declare io_device_id: string | null
  @Attr(null) declare io_device_access_token: string | null
  @Attr(null) declare tank_name: string | null
  @Attr(null) declare reseller_id: string | null
  @Attr(true) declare allow_auto_connect: boolean
  @Attr(false) declare allow_auto_start: boolean
  @Attr(null) declare level_percentage: string | null
  @Attr(null) declare org_group_id: string | null
  @Attr(null) declare hardware_pre_flow_timeout_s: number | null
  @Attr(null) declare hardware_post_flow_timeout_s: number | null
  @Attr(null) declare hardware_residuals_timeout_ms: number | null
  @Attr(null) declare hardware_bt_reconnect_timeout_ms: number | null
  @Attr(null) declare hardware_bt_idle_timeout_ms: number | null
  @Attr(null) declare hardware_pre_flow_timeout_ms: number | null
  @Attr(null) declare hardware_post_flow_timeout_ms: number | null
  @Attr(null) declare hardware_illegal_volume_ml: number | null
  @Attr(null) declare hardware_max_level_mm: number | null
  @Attr(null) declare hardware_analogue_level_max_ma: number | null
  @Attr(false) declare hardware_estop_open_circuit_installed: boolean | null
  @Attr(false) declare hardware_over_ride_installed: boolean | null
  @Attr(false) declare hardware_is_demo_mode: number | null
  @Attr(false) declare hardware_has_bypass: boolean | null
  @Attr(null) declare hardware_pulse_k_factor: number | null
  @Attr(null) declare hardware_pulse_k_factor_unit: number | null
  @Attr(null) declare hardware_max_pump_run_time_minutes: number | null
  @Attr(null) declare hardware_index_start: number | null
  @Attr(null) declare hardware_get_histories_every_x_ms: number | null
  @Attr(null) declare hardware_input_1: number | null
  @Attr(null) declare hardware_input_2: number | null
  @Attr(null) declare hardware_input_3: number | null
  @Attr(null) declare hardware_input_4: number | null

  @Attr(null) declare hardware_nozzle_switch_installed: boolean | null
  @Attr(null) declare hardware_estop_closed_circuit_installed: boolean | null
  @Attr(null) declare hardware_level_installed: boolean | null
  @Num(0) declare hardware_settings_generation: number
  @Num(0) declare hardware_settings_applied_generation: number
  @Attr(false) declare hardware_settings_stale: boolean

  @BelongsTo(() => DeviceModel, 'device_model_id') declare deviceModel: DeviceModel | null
  @BelongsTo(() => Fluid, 'fluid_id') declare fluid: Fluid | null
  @BelongsTo(() => CalibrationOrg, 'calibration_org_id') declare calibrationOrg: CalibrationOrg | null
  @BelongsTo(() => Reseller, 'reseller_id') declare reseller: Reseller | null
  @BelongsTo(() => OrgGroup, 'org_group_id') declare orgGroup: OrgGroup | null

  @HasMany(() => Transaction, 'device_id') declare transactions: Transaction[]
  @HasMany(() => Tank, 'device_id') declare tanks: Tank[]

  @Attr(null) declare tenant_id: string
  @Attr(null) declare org_id: number
  @BelongsTo(() => Tenant, 'tenant_id') declare tenant: Tenant | null
  @BelongsTo(() => Org, 'org_id') declare org: Org | null
}
